// 7-day sensor trend chart with daily rainfall overlay.
//
// Aggregates readings from every multi-probe sensor in the zone (mean VWC
// per 2" / 4" per 30-min bucket), then overlays daily precipitation bars
// from weather_daily. Superintendents use this to see "did last Thursday's
// rain event actually rewet Green #7 at 4"? or did it just surface-sheet?"

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getSensorReadings, getZoneSensors } from '../api/sensors.js';
import { getWeatherHistory, type HistoricalDayRow } from '../api/weather.js';
import type { SensorReadingsSeries, SensorWithLatest } from '@greeniq/shared';
import {
  DARK_CHART_TOKENS,
  darkTooltipContentStyle,
  darkTooltipItemStyle,
  darkTooltipLabelStyle,
  darkLegendWrapperStyle,
} from '../lib/charts-theme';
import { DataCard } from './ui/data-card';

interface Props {
  courseSlug: string;
  zoneId: number;
}

interface ChartRow {
  ts: string;
  dayLabel: string;
  vwc2: number | null;
  vwc4: number | null;
  precipIn?: number;
}

const BUCKET_MINUTES = 30;

function bucketKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCMinutes(Math.floor(d.getUTCMinutes() / BUCKET_MINUTES) * BUCKET_MINUTES, 0, 0);
  return d.toISOString();
}

export function SensorTrendChart({ courseSlug, zoneId }: Props) {
  const [sensors, setSensors] = useState<SensorWithLatest[] | null>(null);
  const [series, setSeries] = useState<Record<number, SensorReadingsSeries[]>>({});
  const [weather, setWeather] = useState<HistoricalDayRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load sensors + their 7-day series + weather history in one pass.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getZoneSensors(courseSlug, zoneId);
        if (cancelled) return;
        setSensors(r.sensors);
        if (r.sensors.length === 0) return;

        const to = new Date();
        const from = new Date(to);
        from.setUTCDate(from.getUTCDate() - 7);
        const [weatherResp, ...seriesResps] = await Promise.all([
          getWeatherHistory(courseSlug, from.toISOString().slice(0, 10), to.toISOString().slice(0, 10)),
          ...r.sensors.map((s) => getSensorReadings(courseSlug, s.sensor.id, '7d')),
        ]);
        if (cancelled) return;
        setWeather(weatherResp.days);
        const map: Record<number, SensorReadingsSeries[]> = {};
        r.sensors.forEach((s, i) => {
          map[s.sensor.id] = seriesResps[i]?.series ?? [];
        });
        setSeries(map);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [courseSlug, zoneId]);

  const rows = useMemo<ChartRow[]>(() => {
    if (!sensors || sensors.length === 0) return [];

    // Bucket VWC readings by depth (2" vs 4") and 30-min window.
    const buckets = new Map<string, { vwc2: number[]; vwc4: number[] }>();
    for (const s of sensors) {
      const depth = s.sensor.depthInches;
      const vwcSeries = series[s.sensor.id]?.find((x) => x.metric === 'vwc');
      if (!vwcSeries) continue;
      for (const p of vwcSeries.points) {
        const key = bucketKey(p.ts);
        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = { vwc2: [], vwc4: [] };
          buckets.set(key, bucket);
        }
        if (depth !== null && depth <= 3) bucket.vwc2.push(p.value);
        else bucket.vwc4.push(p.value);
      }
    }

    const precipByDate = new Map<string, number>();
    for (const d of weather) {
      if (d.precipitationIn !== null && Number.isFinite(d.precipitationIn)) {
        precipByDate.set(d.observationDate, d.precipitationIn);
      }
    }

    const keys = Array.from(buckets.keys()).sort();
    return keys.map((ts) => {
      const b = buckets.get(ts)!;
      const date = ts.slice(0, 10);
      const d = new Date(ts);
      const dayLabel = d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' });
      return {
        ts,
        dayLabel,
        vwc2: b.vwc2.length > 0 ? mean(b.vwc2) : null,
        vwc4: b.vwc4.length > 0 ? mean(b.vwc4) : null,
        precipIn: precipByDate.get(date),
      };
    });
  }, [sensors, series, weather]);

  if (error) {
    return (
      <DataCard className="mt-6" title="7-day moisture trend">
        <p className="text-sm text-[color:var(--color-error)]">Trend chart unavailable: {error}</p>
      </DataCard>
    );
  }

  if (sensors !== null && sensors.length === 0) return null;

  return (
    <DataCard
      className="mt-6"
      title="7-day moisture trend"
      subtitle="VWC per depth · daily rainfall overlay"
    >
      {rows.length === 0 ? (
        <p className="text-sm text-fg-muted">
          Not enough readings yet — need at least one simulator tick across a 7-day window.
        </p>
      ) : (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={DARK_CHART_TOKENS.gridStroke} />
              <XAxis
                dataKey="dayLabel"
                tick={{ fontSize: 10, fill: DARK_CHART_TOKENS.axisColor }}
                stroke={DARK_CHART_TOKENS.axisColor}
                minTickGap={36}
              />
              <YAxis
                yAxisId="left"
                domain={[0, 0.5]}
                tick={{ fontSize: 11, fill: DARK_CHART_TOKENS.axisColor }}
                stroke={DARK_CHART_TOKENS.axisColor}
                width={40}
                label={{
                  value: 'VWC',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: DARK_CHART_TOKENS.axisColor },
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 'dataMax + 0.5']}
                tick={{ fontSize: 11, fill: DARK_CHART_TOKENS.axisColor }}
                stroke={DARK_CHART_TOKENS.axisColor}
                width={40}
                label={{
                  value: 'Rain (in)',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 11, fill: DARK_CHART_TOKENS.axisColor },
                }}
              />
              <Tooltip
                contentStyle={darkTooltipContentStyle}
                itemStyle={darkTooltipItemStyle}
                labelStyle={darkTooltipLabelStyle}
                formatter={(value, name) => {
                  if (value === null || value === undefined) return ['—', String(name)];
                  const num = typeof value === 'number' ? value : Number(value);
                  if (!Number.isFinite(num)) return ['—', String(name)];
                  if (name === 'Rain') return [`${num.toFixed(2)}″`, name];
                  return [num.toFixed(3), String(name)];
                }}
              />
              <Legend wrapperStyle={darkLegendWrapperStyle} />
              <Bar
                yAxisId="right"
                dataKey="precipIn"
                name="Rain"
                fill={DARK_CHART_TOKENS.rainBar}
                opacity={0.7}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="vwc2"
                name="VWC 2″"
                stroke={DARK_CHART_TOKENS.greenPrimary}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="vwc4"
                name="VWC 4″"
                stroke={DARK_CHART_TOKENS.greenDeep}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </DataCard>
  );
}

function mean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length;
}
